import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import type { FC } from 'react';
import type { SearchCategory, SearchTab } from './useSearchResults';

interface Props {
  tabs: Array<SearchTab>;
  activeCategory: SearchCategory;
  onSelect: (category: SearchCategory) => void;
  fullWidth?: boolean;
}

export const SearchTabs: FC<Props> = ({ tabs, activeCategory, onSelect, fullWidth = false }) => {
  if (tabs.length === 0) return null;

  return (
    // Not a tablist: that pattern wants a focusable tab, and focus belongs to the input here.
    /* biome-ignore lint/a11y/useSemanticElements: a <fieldset> needs a <legend> and pulls form
       semantics into a search panel that has no form */
    <Bar role="group" aria-label="Result categories" fullWidth={fullWidth}>
      <Pills fullWidth={fullWidth}>
        {tabs.map((tab) => (
          <Pill
            key={tab.key}
            type="button"
            tabIndex={-1}
            isActive={tab.key === activeCategory}
            fullWidth={fullWidth}
            aria-pressed={tab.key === activeCategory}
            // Without this the click pulls focus off the input and the keyboard stops working.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onSelect(tab.key)}
          >
            {tab.title} <Count>{`${tab.count}${tab.hasMore ? '+' : ''}`}</Count>
          </Pill>
        ))}
      </Pills>
      {!fullWidth && <Hint>tab to switch</Hint>}
    </Bar>
  );
};

const Bar = styled.div<{ fullWidth: boolean }>`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: ${({ fullWidth }) => (fullWidth ? '9px 14px' : '9px 14px')};
  background: ${({ fullWidth }) => (fullWidth ? 'transparent' : RacemapColors.LightLightLightGray)};
  border-bottom: 1px solid ${RacemapColors.LightGray};
`;

// On the phone the pills are the whole bar and scroll sideways instead of wrapping.
const Pills = styled.div<{ fullWidth: boolean }>`
  display: flex;
  gap: ${({ fullWidth }) => (fullWidth ? '7px' : '6px')};
  min-width: 0;
  overflow-x: ${({ fullWidth }) => (fullWidth ? 'auto' : 'visible')};
  scrollbar-width: none;
`;

const Pill = styled.button<{ isActive: boolean; fullWidth: boolean }>`
  flex: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  font-weight: ${({ isActive }) => (isActive ? 600 : 500)};
  color: ${({ isActive }) => (isActive ? '#fff' : RacemapColors.DarkGray)};
  background: ${({ isActive }) => (isActive ? RacemapColors.PaleBlue : 'transparent')};
  ${({ fullWidth, isActive }) =>
    fullWidth
      ? `border-radius: 20px;
         padding: 7px 14px;
         font-size: 13px;
         min-height: 40px;
         ${isActive ? '' : `background: ${RacemapColors.LightLightGray};`}`
      : `border-radius: 4px;
         padding: 3px 10px;
         font-size: 11.5px;`}
`;

const Count = styled.span`
  color: inherit;
  opacity: 0.7;
`;

const Hint = styled.span`
  flex: none;
  color: ${RacemapColors.DarkGray};
  font-size: 11px;
  font-weight: 500;
`;
